import dayjs from "dayjs";
import isArray from "lodash/isArray.js";
import { Button, DatePicker, InputNumber, Select } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useState } from "react";

const MODE_OPTIONS = [
  { value: "PAST", label: "Past" },
  { value: "OLDER", label: "Older Than" },
  { value: "FUTURE", label: "Future" },
  { value: "LATER", label: "Later Than" },
  { value: "BETWEEN", label: "Between" },
];

const RELATIVE_MODES = ["PAST", "OLDER", "FUTURE", "LATER"];

const DEFAULT_RELATIVE_FILTER = {
  mode: "FUTURE",
  quantity: 15,
  unit: "DAY",
};

const UNIT_OPTIONS = [
  { value: "DAY", label: "Day(s)" },
  { value: "WEEK", label: "Week(s)" },
  { value: "MONTH", label: "Month(s)" },
  { value: "YEAR", label: "Year(s)" },
];

const normalizeUnit = (unit) => {
  if (!unit) {
    return DEFAULT_RELATIVE_FILTER.unit;
  }

  return unit.endsWith("S") ? unit.slice(0, -1) : unit;
};

const buildRelativeValue = ({ mode, quantity, unit }) => {
  const normalizedQuantity = Math.max(1, Number(quantity) || 1);
  const storedUnit = normalizedQuantity === 1 ? unit : `${unit}S`;

  return `${mode}_${normalizedQuantity}_${storedUnit}`;
};

const getRelativeValueFromFilter = (value) => {
  if (isArray(value) || typeof value !== "string") {
    return DEFAULT_RELATIVE_FILTER;
  }

  const [mode, quantity, unit] = value.split("_");

  if (!RELATIVE_MODES.includes(mode)) {
    return DEFAULT_RELATIVE_FILTER;
  }

  const parsedQuantity = Number(quantity);

  return {
    mode,
    quantity: Number.isInteger(parsedQuantity) && parsedQuantity > 0
      ? parsedQuantity
      : DEFAULT_RELATIVE_FILTER.quantity,
    unit: normalizeUnit(unit),
  };
};

const getModeFromValue = (value) => {
  if (isArray(value)) {
    return "BETWEEN";
  }

  const [mode] = (value || "").split("_");

  if (RELATIVE_MODES.includes(mode)) {
    return mode;
  }

  return DEFAULT_RELATIVE_FILTER.mode;
};

const getRangeFromValue = (value) => {
  if (isArray(value)) {
    return [dayjs.unix(value[0]), dayjs.unix(value[1])];
  }

  return [dayjs().subtract(1, "week"), dayjs()];
};

export const DateFilter = ({ config, defaultValue = null, onFilter }) => {
  const initialMode = getModeFromValue(defaultValue);
  const initialRelativeFilter = getRelativeValueFromFilter(defaultValue);
  const [mode, setMode] = useState(initialMode);
  const [quantity, setQuantity] = useState(initialRelativeFilter.quantity);
  const [unit, setUnit] = useState(initialRelativeFilter.unit);
  const [range, setRange] = useState(getRangeFromValue(defaultValue));

  if (mode === "BETWEEN") {
    return (
      <>
        <Select
          onChange={(nextMode) => {
            setMode(nextMode);
          }}
          options={MODE_OPTIONS}
          size="small"
          value={mode}
        />
        <DatePicker.RangePicker
          onChange={setRange}
          style={{ marginTop: "8px", width: "100%" }}
          value={range}
        />
        <Button
          icon={<FilterOutlined />}
          onClick={() => {
            if (!range?.[0] || !range?.[1]) {
              return;
            }

            return onFilter({
              dataIndex: config.dataIndex,
              value: [range[0].unix(), range[1].unix()],
            });
          }}
          size="small"
          style={{ marginTop: "8px", width: "100%" }}
          type="primary"
        >
          Filter
        </Button>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", gap: "8px" }}>
        <Select
          onChange={(nextMode) => {
            setMode(nextMode);
          }}
          options={MODE_OPTIONS}
          size="small"
          style={{ minWidth: "96px" }}
          value={mode}
        />
        <InputNumber
          min={1}
          onChange={(nextQuantity) => {
            setQuantity(nextQuantity || 1);
          }}
          size="small"
          style={{ minWidth: "36px", width: "72px" }}
          value={quantity}
        />
        <Select
          onChange={setUnit}
          options={UNIT_OPTIONS}
          size="small"
          value={unit}
        />
      </div>
      <Button
        icon={<FilterOutlined />}
        onClick={() => {
          return onFilter({
            dataIndex: config.dataIndex,
            value: buildRelativeValue({ mode, quantity, unit }),
          });
        }}
        size="small"
        style={{ marginTop: "8px", width: "100%" }}
        type="primary"
      >
        Filter
      </Button>
    </>
  );
};
